/**
 * 和了判定 + 手牌分解
 *
 * 和了（agari）条件：手牌能拆成「4 面子 + 1 雀头」（普通形），或满足特殊牌形。
 *
 * 为什么要「分解」：同一手牌可能有多种拆法，不同拆法对应不同役种和符数。
 *   例：111222333m 可以拆成 3 个刻子，也可能拆成 3 个顺子（123m×3）
 *         → 前者可能是「三暗刻」，后者可能是「一气通贯」
 *   所以必须枚举所有拆法，逐个算役取最高番。
 *
 * 本模块提供：
 *   · decompose()  —— 枚举所有可能的（面子集合 + 雀头）拆法
 *   · isAgari()    —— 快速判定是否和了（不枚举全部拆法）
 *   · decomposeAll() —— 含特殊牌形（七对子/国士）
 */

import {
  Counts,
  NUM_TILE_TYPES,
  cloneCounts,
  countsTotal,
  hasOverFour,
  indexToName,
} from './tile.js';

// ════════════════════════════════════════════════════════════════
// 类型定义
// ════════════════════════════════════════════════════════════════

/** 面子类型 */
export type MeldKind = 'shuntsu' | 'koutsu' | 'kantsu';

/** 面子（一组牌） */
export interface Mentsu {
  /** 类型：顺子 / 刻子 / 杠子 */
  readonly kind: MeldKind;
  /** 起始索引（顺子=最小牌，刻子/杠子=该牌） */
  readonly start: number;
  /** 是否副露（鸣牌而来） */
  readonly open: boolean;
  /** 牌名列表（便于显示，如 ['1m','2m','3m']） */
  readonly names: readonly string[];
  /**
   * 被鸣的那张牌在该面子中的位置（0/1/2）
   *
   *   · 顺子（吃）：被吃进的牌在 [start, start+1, start+2] 里的下标
   *     —— 例：吃 3s4s5s 且被吃的是 5s → calledIndex = 2
   *   · 刻子/杠子：0（牌面全相同，位置无意义）
   *
   * UI 据此决定副露里哪一张「横置」。仅副露（open=true）时有意义。
   */
  readonly calledIndex?: number;
  /**
   * 该面子里含的赤宝牌数量（顺子/刻子 0~1，杠理论上 0~1）。
   * Mentsu 不保留 Tile 对象、会丢失 red 标识 → 鸣牌成形时把赤数记在这里，
   * 供和牌时统计赤宝番（detail 汇总 m.red）。可选，默认 0。
   */
  readonly red?: number;
}

/** 一种完整的和了分解方式 */
export interface Decomposition {
  /** 4 个面子（含副露） */
  readonly mentsu: readonly Mentsu[];
  /** 雀头（对子的索引），无雀头时为 null（七对子/国士） */
  readonly pair: number | null;
  /** 牌形类型 */
  readonly form: 'regular' | 'chiitoitsu' | 'kokushi';
  /**
   * 七对子的对子列表（索引数组）。
   *
   * ⚠️ 七对子**不能**用 `mentsu` 表示：
   *    Mentsu 只有 shuntsu/koutsu/kantsu 三种，
   *    把 2 张的对子塞成 koutsu 会导致"用牌校验"多算 1 张
   *    （踩过：11223344556677m 的 chiitoitsu 分解算出 21 张牌）。
   *    所以七对子用本字段表示，`mentsu` 留空。
   */
  readonly pairs?: readonly number[];
}

/** 和了判定结果 */
export interface AgariCheck {
  /** 是否和了 */
  readonly isAgari: boolean;
  /** 可用的分解方式数量（0 = 未和） */
  readonly decompositionCount: number;
}

// ════════════════════════════════════════════════════════════════
// 面子工具
// ════════════════════════════════════════════════════════════════

/** 创建顺子 */
function makeShuntsu(start: number, open = false): Mentsu {
  return {
    kind: 'shuntsu',
    start,
    open,
    names: [start, start + 1, start + 2].map(indexToName),
  };
}

/** 创建刻子 */
function makeKoutsu(start: number, open = false): Mentsu {
  return { kind: 'koutsu', start, open, names: [indexToName(start)] };
}

/** 创建杠子 */
function makeKantsu(start: number, open = false): Mentsu {
  return { kind: 'kantsu', start, open, names: [indexToName(start)] };
}

/** 面子是否为顺子 */
export function isShuntsu(m: Mentsu): boolean {
  return m.kind === 'shuntsu';
}

/** 面子是否为刻子或杠子（刻子类） */
export function isKoutsuLike(m: Mentsu): boolean {
  return m.kind === 'koutsu' || m.kind === 'kantsu';
}

/** 面子是否包含幺九牌 */
export function containsYaochu(m: Mentsu): boolean {
  return m.names.some((n) => {
    const idx = m.start;
    if (m.kind === 'shuntsu') return false; // 顺子首尾判断见下
    return idx >= 27 || (idx % 9 === 0) || (idx % 9 === 8);
  });
}

// ════════════════════════════════════════════════════════════════
// 和了判定（快速，不枚举全部拆法）
// ════════════════════════════════════════════════════════════════

/**
 * 判定是否和了
 *
 * @param counts 手牌计数（34 长度，**不含副露**）
 * @param meldCount 副露面子数
 */
export function isAgari(counts: Counts, meldCount = 0): boolean {
  const total = countsTotal(counts) + meldCount * 3;
  if (total !== 14) return false;
  if (hasOverFour(counts)) return false;

  const needMentsu = 4 - meldCount;
  if (needMentsu < 0) return false;

  // 普通形：找雀头 + needMentsu 个面子
  const work = cloneCounts(counts);
  for (let pair = 0; pair < NUM_TILE_TYPES; pair++) {
    if (work[pair] < 2) continue;
    work[pair] -= 2;
    const ok = canFormMentsu(work, needMentsu);
    work[pair] += 2;
    if (ok) return true;
  }

  // 七对子 / 国士（必须门清）
  if (meldCount === 0) {
    if (isChiitoitsu(counts)) return true;
    if (isKokushi(counts)) return true;
  }

  return false;
}

/** 快速判定：能否从 counts 中恰好取出 n 个面子 */
function canFormMentsu(counts: number[], n: number): boolean {
  if (n === 0) return counts.every((c) => c === 0);

  // 找第一张有牌的位置
  let i = 0;
  while (i < NUM_TILE_TYPES && counts[i] === 0) i++;
  if (i >= NUM_TILE_TYPES) return false;

  const num = i % 9;
  const isHonorTile = i >= 27;

  // 刻子
  if (counts[i] >= 3) {
    counts[i] -= 3;
    if (canFormMentsu(counts, n - 1)) {
      counts[i] += 3;
      return true;
    }
    counts[i] += 3;
  }

  // 顺子
  if (!isHonorTile && num <= 6 && counts[i + 1] > 0 && counts[i + 2] > 0) {
    counts[i]--;
    counts[i + 1]--;
    counts[i + 2]--;
    if (canFormMentsu(counts, n - 1)) {
      counts[i]++;
      counts[i + 1]++;
      counts[i + 2]++;
      return true;
    }
    counts[i]++;
    counts[i + 1]++;
    counts[i + 2]++;
  }

  return false;
}

// ════════════════════════════════════════════════════════════════
// 手牌分解（枚举所有拆法）
// ════════════════════════════════════════════════════════════════

/**
 * 枚举手牌的所有分解方式
 *
 * @param counts 手牌计数（不含副露），必须总和 = 14 - meldCount*3
 * @param melds 副露的面子列表（会被合并进每种分解结果）
 * @returns 所有可能的分解（普通形 + 七对子 + 国士）
 */
export function decomposeAll(counts: Counts, melds: readonly Mentsu[] = []): Decomposition[] {
  const results: Decomposition[] = [];
  const meldCount = melds.length;
  const total = countsTotal(counts) + meldCount * 3;
  if (total !== 14 || hasOverFour(counts)) return results;

  const needMentsu = 4 - meldCount;
  if (needMentsu < 0) return results;

  // ── 普通形 ──
  const work = cloneCounts(counts);
  for (let pair = 0; pair < NUM_TILE_TYPES; pair++) {
    if (work[pair] < 2) continue;
    work[pair] -= 2;
    const found: Mentsu[][] = [];
    collectMentsu(work, needMentsu, [], found, [...melds]);
    for (const m of found) {
      results.push({ mentsu: m, pair, form: 'regular' });
    }
    work[pair] += 2;
  }

  // ── 七对子 / 国士（必须门清）──
  if (meldCount === 0) {
    if (isChiitoitsu(counts)) {
      // ⚠️ 七对子：7 个对子用 `pairs` 表示。
      //    不能用 makeKoutsu 冒充（Mentsu 只有 顺子/刻子/杠子，没有对子），
      //    否则"用牌校验"会多算 1 张/组（踩过：11223344556677m 算出 21 张）。
      const pairs: number[] = [];
      for (let i = 0; i < NUM_TILE_TYPES; i++) {
        if (counts[i] >= 2) pairs.push(i);
      }
      results.push({ mentsu: [], pair: null, pairs, form: 'chiitoitsu' });
    }
    if (isKokushi(counts)) {
      // ⚠️ 国士无双：13 种幺九各 1 张 + 其中 1 种 2 张 → 用 pairs 表示那张重复的牌
      const pairs: number[] = [];
      for (let i = 0; i < NUM_TILE_TYPES; i++) {
        if (counts[i] === 2) pairs.push(i);
      }
      results.push({ mentsu: [], pair: null, pairs, form: 'kokushi' });
    }
  }

  return results;
}

/** 递归收集所有面子组合 */
function collectMentsu(
  counts: number[],
  remaining: number,
  current: Mentsu[],
  out: Mentsu[][],
  melds: Mentsu[],
): void {
  if (remaining === 0) {
    if (counts.every((c) => c === 0)) {
      out.push([...melds, ...current]);
    }
    return;
  }

  let i = 0;
  while (i < NUM_TILE_TYPES && counts[i] === 0) i++;
  if (i >= NUM_TILE_TYPES) return;

  const num = i % 9;
  const isHonorTile = i >= 27;

  // 刻子
  if (counts[i] >= 3) {
    counts[i] -= 3;
    current.push(makeKoutsu(i));
    collectMentsu(counts, remaining - 1, current, out, melds);
    current.pop();
    counts[i] += 3;
  }

  // 顺子
  if (!isHonorTile && num <= 6 && counts[i + 1] > 0 && counts[i + 2] > 0) {
    counts[i]--;
    counts[i + 1]--;
    counts[i + 2]--;
    current.push(makeShuntsu(i));
    collectMentsu(counts, remaining - 1, current, out, melds);
    current.pop();
    counts[i]++;
    counts[i + 1]++;
    counts[i + 2]++;
  }
}

// ════════════════════════════════════════════════════════════════
// 特殊牌形判定
// ════════════════════════════════════════════════════════════════

/** 七对子：7 个不同的对子（且必须门清、14 张） */
export function isChiitoitsu(counts: Counts): boolean {
  if (countsTotal(counts) !== 14) return false;
  let pairs = 0;
  for (let i = 0; i < NUM_TILE_TYPES; i++) {
    const c = counts[i];
    if (c === 0) continue;
    if (c !== 2) return false; // 必须是恰好 2 张（4 张不行）
    pairs++;
  }
  return pairs === 7;
}

/** 国士无双：13 种幺九各 ≥1 张，其中 1 种 =2 张（其余 1 张） */
export function isKokushi(counts: Counts): boolean {
  if (countsTotal(counts) !== 14) return false;
  const yaochu = [0, 8, 9, 17, 18, 26, 27, 28, 29, 30, 31, 32, 33];
  let pairs = 0;
  for (let i = 0; i < NUM_TILE_TYPES; i++) {
    const c = counts[i];
    if (!yaochu.includes(i)) {
      if (c > 0) return false; // 有非幺九牌
      continue;
    }
    if (c === 0) return false; // 缺某种幺九
    if (c === 2) pairs++;
    else if (c !== 1) return false;
  }
  return pairs === 1;
}

/** 国士无双十三面（13 种各 1 张 + 任一张成对即和）—— 用于役满判定 */
export function isKokushi13Wait(counts: Counts): boolean {
  if (countsTotal(counts) !== 14) return false;
  const yaochu = [0, 8, 9, 17, 18, 26, 27, 28, 29, 30, 31, 32, 33];
  let pairs = 0;
  for (let i = 0; i < NUM_TILE_TYPES; i++) {
    const c = counts[i];
    if (!yaochu.includes(i)) {
      if (c > 0) return false;
      continue;
    }
    if (c === 0) return false;
    if (c >= 2) pairs++;
  }
  // 十三面：任意一种成对即可，所以这里只检查"13 种齐 + 有对子"
  // 真正的十三面判定需要知道"和了牌是哪张"（是否打断了原有的对子）
  return pairs === 1;
}

// ════════════════════════════════════════════════════════════════
// 辅助
// ════════════════════════════════════════════════════════════════

/** 分解结果 → 可读字符串（调试用） */
export function describeDecomposition(d: Decomposition): string {
  const parts = d.mentsu.map((m) => {
    if (m.kind === 'shuntsu') return m.names.join('');
    return `${m.names[0]}×3`;
  });
  if (d.pair !== null) parts.push(`${indexToName(d.pair)}×2`);
  return `[${d.form}] ${parts.join(' ')}`;
}

/** 快速检查（返回布尔 + 拆法数量） */
export function checkAgari(counts: Counts, melds: readonly Mentsu[] = []): AgariCheck {
  const ds = decomposeAll(counts, melds);
  return { isAgari: ds.length > 0, decompositionCount: ds.length };
}

// ════════════════════════════════════════════════════════════════
// 分解 → 用牌统计（校验/展示用）
// ════════════════════════════════════════════════════════════════

/**
 * 统计一个分解用掉的牌（34 长度计数数组）
 *
 * ⚠️ 七对子/国士用 `pairs`，普通形用 `mentsu` —— 两者都要算，
 *    否则校验会失败（踩过：chiitoitsu 算出 21 张）。
 */
export function decompositionTiles(d: Decomposition): number[] {
  const used = new Array(34).fill(0);

  for (const m of d.mentsu) {
    if (m.kind === 'shuntsu') {
      used[m.start]++;
      used[m.start + 1]++;
      used[m.start + 2]++;
    } else if (m.kind === 'koutsu') {
      used[m.start] += 3;
    } else {
      used[m.start] += 4; // kantsu
    }
  }

  if (d.pair !== null) used[d.pair] += 2;

  // 七对子 / 国士的对子
  if (d.pairs) {
    for (const p of d.pairs) used[p] += 2;
  }

  return used;
}

/** 校验分解是否与手牌一致 */
export function isDecompositionValid(d: Decomposition, counts: readonly number[]): boolean {
  const used = decompositionTiles(d);
  for (let i = 0; i < 34; i++) {
    if (used[i] !== (counts[i] ?? 0)) return false;
  }
  return true;
}
